import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { combineLatest, ReplaySubject, takeUntil } from 'rxjs';

import { Badge } from '../../../../../models/badge';
import { BadgesService } from '../../../../../services/badges.service';
import { ConfirmationPage } from '../../../../modals/confirmation/confirmation.page';
import { ErrorPage } from '../../../../modals/error/error.page';

interface EditBadgeViewModel {
  id: string;
  categoryId: string;
}

interface EditBadgeForm {
  name: FormControl<string>;
  description: FormControl<string>;
  tokenize: FormControl<boolean>;
  iconOutlined?: FormControl<string>;
  iconColored?: FormControl<string>;
}

@Component({
  selector: 'app-edit-badge',
  templateUrl: './edit-badge.page.html',
  styleUrls: ['./edit-badge.page.scss'],
})
export class EditBadgePage implements OnInit, OnDestroy {
  viewModel: EditBadgeViewModel;
  form: FormGroup;
  loading = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private activatedRoute: ActivatedRoute,
    private badgesService: BadgesService,
    private modalController: ModalController,
    private router: Router
  ) {}

  ngOnInit() {
    this.form = new FormGroup<EditBadgeForm>({
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      description: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      tokenize: new FormControl(false, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      iconOutlined: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      iconColored: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });

    combineLatest([this.activatedRoute.data, this.activatedRoute.queryParams])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([data, queryParams]) => {
        const badge: Badge = data.badge ?? {};

        this.viewModel = {
          id: badge.id,
          categoryId: badge.categoryId ?? queryParams.categoryId,
        };

        this.form.patchValue(badge);
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  async handleBadgeSave() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const badge = { ...this.form.value, id: this.viewModel.id };

    this.loading = true;

    this.badgesService
      .upsertBadge(badge.categoryId ?? this.viewModel.categoryId, badge, {
        ignoreErrors: true,
      })
      .subscribe({
        next: () =>
          this.router.navigate(['/badges/admin'], { replaceUrl: true }),
        error: () =>
          this.presentErrorModal(
            'Ocorreu um erro ao salvar o registro. Por favor, tente novamente mais tarde.'
          ),
        complete: () => (this.loading = false),
      });
  }

  async handleBadgeDelete() {
    const modal = await this.modalController.create({
      component: ConfirmationPage,
      cssClass: 'app-auto-height-modal',
      componentProps: {
        icon: '../../../assets/icons/delete.svg',
        color: 'danger',
        title: 'Apagar medalha',
        description:
          'Você deseja apagar a medalha? Essa ação não pode ser desfeita.',
        negativeButtonText: 'Cancelar',
        positiveButtonText: 'Excluir',
      },
    });

    await modal.present();

    const { data: confirmed } = await modal.onWillDismiss();
    if (confirmed) {
      this.badgesService
        .deleteBadge(this.viewModel.id, {
          ignoreErrors: true,
        })
        .subscribe({
          next: () =>
            this.router.navigate(['/badges/admin'], { replaceUrl: true }),
          error: ({ error }) =>
            this.presentErrorModal(
              error?.message ??
                'Ocorreu um erro ao excluir o registro. Por favor, tente novamente mais tarde.'
            ),
        });
    }
  }

  private async presentErrorModal(message: string) {
    const modal = await this.modalController.create({
      component: ErrorPage,
      cssClass: 'app-auto-height-modal',
      componentProps: {
        message,
      },
    });

    await modal.present();
  }
}
